import classNames from "classnames";
import { DateTime } from "luxon";
import { useState } from "react";
import { Constants } from "../../../../../../../../../common/constants";
import { EventDisplayData } from "../../../../../../../../../common/sesh_types";
import { NativeEventDisplayData } from "../../../../../../../../../src/shard_api/routes/getNativeEvents";
import { RemovingThrobber } from "../../../../../removing_throbber";
import { WithDropdownBase } from "../../../../base_components/with_dropdown_base";
import { isSeshEvent } from "../utils/is_sesh_event";
import {
	DiscordEventItemDropdown,
	SeshEventItemDropdown,
} from "./calendar_event_item_dropdown";
import { CalendarEventDisplayData } from "./calendar_page";

export function CalendarEventItem(props: {
	event: CalendarEventDisplayData;
	dropdown_parent_id?: string;
	is_today_or_future: boolean;
}) {
	if (isSeshEvent(props.event)) {
		return <CalendarSeshEventItem {...props} event={props.event} />;
	}

	return <CalendarDiscordEventItem {...props} event={props.event} />;
}

export function CalendarSeshEventItem(props: {
	event: EventDisplayData;
	dropdown_parent_id?: string;
	is_today_or_future: boolean;
}) {
	const [is_removing, setIsRemoving] = useState(false);

	return (
		<WithDropdownBase
			id={`${props.event.message_id}_${props.event.start_time_str}`}
			x_alignment="auto"
			y_alignment="middle"
			dropdown_element={
				<SeshEventItemDropdown
					event={props.event}
					is_removing={is_removing}
					setIsRemoving={setIsRemoving}
				/>
			}
			parent_id={props.dropdown_parent_id}
			disabled={is_removing}
		>
			{(is_active) => (
				<EventCellItem
					name={props.event.name}
					is_active={is_active}
					color={props.event.color}
					start_time_str={props.event.start_time_str}
					end_time_str={props.event.end_time_str}
					is_removing={is_removing}
					is_today_or_future={props.is_today_or_future}
				/>
			)}
		</WithDropdownBase>
	);
}

export function CalendarDiscordEventItem(props: {
	event: NativeEventDisplayData;
	dropdown_parent_id?: string;
	is_today_or_future: boolean;
}) {
	const [is_removing, setIsRemoving] = useState(false);

	return (
		<WithDropdownBase
			id={`${props.event.id}_${props.event.start_time_str}`}
			x_alignment="auto"
			y_alignment="middle"
			dropdown_element={
				<DiscordEventItemDropdown
					event={props.event}
					is_removing={is_removing}
					setIsRemoving={setIsRemoving}
				/>
			}
			parent_id={props.dropdown_parent_id}
			disabled={is_removing}
		>
			{(is_active) => (
				<EventCellItem
					name={
						<span className="flex items-center gap-1">
							<span className="text-[0.6rem]">
								<i className="fab fa-discord" />
							</span>
							{props.event.name}
						</span>
					}
					is_active={is_active}
					color={Constants.DISCORD_BLURPLE}
					start_time_str={props.event.start_time_str}
					end_time_str={props.event.end_time_str}
					is_removing={is_removing}
					is_today_or_future={props.is_today_or_future}
				/>
			)}
		</WithDropdownBase>
	);
}

function EventCellItem(props: {
	is_active: boolean;
	is_removing: boolean;
	name: JSX.Element | string;
	color: string;
	is_today_or_future: boolean;
	start_time_str: string;
	end_time_str: string;
}) {
	const event_start_time = DateTime.fromJSDate(
		new Date(props.start_time_str)
	);

	const event_end_time = props.end_time_str
		? DateTime.fromJSDate(new Date(props.end_time_str))
		: event_start_time;

	const time = event_start_time
		.toLocaleString({
			timeStyle: "short",
		})
		.toLowerCase()
		.replace(" ", "");

	let time_string = time.includes(":00") ? time.replace(":00", "") : time;

	return (
		<div
			style={{
				borderLeftColor: props.color,
			}}
			className={classNames(
				"relative mb-[1px] flex w-full gap-2 whitespace-nowrap rounded-sm border-l-[3px] px-1 py-[1px] text-left text-xs transition-all hover:bg-base-light",
				{
					"bg-base-light": props.is_active,
					"bg-base-semidark": !props.is_active,
					"opacity-50":
						props.is_today_or_future &&
						event_end_time < DateTime.local(),
				}
			)}
		>
			<div className="overflow-hidden overflow-ellipsis font-semibold">
				{props.name}
			</div>
			<div className="ml-auto flex-shrink-0 text-content-secondary">
				{time_string}
			</div>
			{props.is_removing && <RemovingThrobber />}
			{props.is_removing && (
				<div className="absolute left-0 top-0 h-full w-full bg-warning/10"></div>
			)}
		</div>
	);
}
